import templateUrl from './engagement-claim-modal.html'
import './engagement-claim-modal.scss'

const HUMAN_CHECK_WAIT_MS = 10000

const EngagementClaimModalComponent = {
  bindings: {
    projectId: '<',
    mode: '<'
  },
  require: {
    modalCtrl: '^^cdblModal'
  },
  templateUrl,
  controller: class EngagementClaimModal {
    constructor ($element, $timeout, Configuration, ProjectClaimsService, ProjectService, TurnstileService, UserService) {
      'ngInject'
      this.$element = $element
      this.$timeout = $timeout
      this.Configuration = Configuration
      this.TurnstileService = TurnstileService
      this.ProjectService = ProjectService
      this.ProjectClaimsService = ProjectClaimsService
      this.UserService = UserService
      this.loading = false
      this.error = null
    }

    $onInit () {
      this.humanCheckSettled = this.mode !== 'claim'
      this.turnstileToken = null
      this.widgetId = null
    }

    $postLink () {
      if (this.mode !== 'claim') {
        return
      }
      this.waitForHumanCheck()
      this.TurnstileService.render(this.$element[0].querySelector('.turnstile-widget'), {
        action: 'engagement_claim',
        appearance: 'interaction-only',
        'refresh-expired': 'auto',
        callback: token => this.setToken(token),
        'expired-callback': () => this.clearToken(),
        'timeout-callback': () => this.clearToken(),
        'before-interactive-callback': () => this.holdForChallenge(),
        'after-interactive-callback': () => this.resumeAfterChallenge(),
        'error-callback': () => {
          this.settleHumanCheck()
          return true
        },
        'unsupported-callback': () => this.settleHumanCheck()
      })
        .then(widgetId => {
          this.widgetId = widgetId
        })
        .catch(err => {
          console.error(err)
          this.settleHumanCheck()
        })
    }

    $onDestroy () {
      this.$timeout.cancel(this.humanCheckFallback)
      if (this.widgetId !== null) {
        this.TurnstileService.remove(this.widgetId)
      }
    }

    // The button waits for the token, but never for good: if the widget fails or hangs, the
    // request goes without a token and the backend decides, so claims work with enforcement off.
    waitForHumanCheck () {
      this.humanCheckSettled = false
      this.$timeout.cancel(this.humanCheckFallback)
      this.humanCheckFallback = this.$timeout(() => {
        this.humanCheckSettled = true
      }, HUMAN_CHECK_WAIT_MS)
    }

    // The expert is solving a challenge, so the fallback mustn't enable the button without a token.
    // If they never finish, Turnstile calls timeout-callback, which starts the fallback again.
    holdForChallenge () {
      this.$timeout(() => {
        this.$timeout.cancel(this.humanCheckFallback)
        if (!this.turnstileToken) {
          this.humanCheckSettled = false
        }
      })
    }

    resumeAfterChallenge () {
      this.$timeout(() => {
        if (!this.turnstileToken) {
          this.waitForHumanCheck()
        }
      })
    }

    settleHumanCheck () {
      this.$timeout(() => {
        this.humanCheckSettled = true
      })
    }

    setToken (token) {
      this.$timeout(() => {
        this.turnstileToken = token
        this.humanCheckSettled = true
      })
    }

    clearToken () {
      this.$timeout(() => {
        this.turnstileToken = null
      })
      this.waitForHumanCheck()
    }

    // A token works once, and the backend has already spent it even if the claim then failed.
    resetHumanCheck () {
      this.turnstileToken = null
      if (this.widgetId === null) {
        return
      }
      this.waitForHumanCheck()
      this.TurnstileService.reset(this.widgetId)
    }

    // Human-readable window for the pre-claim warning copy (e.g. "1 hour",
    // "90 minutes"). Null when the window is unknown.
    get claimExpiryLabel () {
      const minutes = this.ProjectClaimsService.claimExpiryMinutes
      if (!minutes) {
        return null
      }
      if (minutes % 60 === 0) {
        const hours = minutes / 60
        return `${hours} hour${hours === 1 ? '' : 's'}`
      }
      return `${minutes} minute${minutes === 1 ? '' : 's'}`
    }

    handleAction () {
      this.loading = true
      this.error = null

      const action = this.mode === 'claim'
        ? this.ProjectClaimsService.claimProject(this.projectId, this.turnstileToken)
        : this.ProjectClaimsService.unclaimProject(this.projectId)

      action
        .then(response => {
          this.modalCtrl.resolve(response)
          Object.assign(this.ProjectService.project, { ...response.project, engagementClaims: response.engagementClaims })
          delete response.expert.id
          Object.assign(this.UserService.user, response.expert)
        })
        .catch(err => {
          this.error = err.data?.error === 'human_check_failed'
            ? 'Please try again.'
            : err.message || 'An error occurred. Please try again.'
          console.error(err)
          if (this.mode === 'claim') {
            this.resetHumanCheck()
          }
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}

export default EngagementClaimModalComponent
